/* eslint-disable react/no-unstable-nested-components */
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import { Field } from "redux-form";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField";
import { useMaterialUIController } from "context";

export const SUPPORT_CASE_STATES = ["new", "update", "deleted"];

const SUPPORT_CASE_STATUS_LABEL_KEYS = {
  new: "emailSupportCaseStatusNew",
  update: "emailSupportCaseStatusUpdate",
  deleted: "emailSupportCaseStatusDeleted",
};

const SUPPORT_CASE_TEXT_FIELDS = [
  { key: "subject", labelKey: "emailFieldSubjectSuffix" },
  { key: "emailTitle", labelKey: "emailFieldEmailTitle" },
  { key: "emailText", labelKey: "emailFieldEmailText" },
  { key: "emailTextClient", labelKey: "emailFieldEmailTextClient" },
  { key: "stateSolvedLabel", labelKey: "emailFieldStateSolvedLabel" },
];

const SUPPORTED_LOCALES = ["es", "en"];

const SUPPORT_CASE_DEFAULT_TEXTS = {
  new: {
    es: {
      subject: "Nuevo caso de soporte",
      emailTitle: "Nuevo caso de soporte",
      emailText: "Se ha creado un nuevo caso de soporte para tu reserva.",
      emailTextClient:
        "Nuestro equipo de soporte se pondra en contacto contigo lo antes posible para resolverlo.",
      stateSolvedLabel:
        "Su caso de soporte ha sido marcado como resuelto. Si consideras que tu incidencia no ha sido resuelta, por favor, ponte en contacto con nuestro equipo de soporte a traves de la plataforma.",
    },
    en: {
      subject: "New support case",
      emailTitle: "New support case",
      emailText: "A new support case has been created for your reservation.",
      emailTextClient:
        "Our support team will get in touch with you as soon as possible to resolve it.",
      stateSolvedLabel:
        "Your support case has been marked as solved. If you consider that your issue has not been resolved, please contact our support team through the platform.",
    },
  },
  update: {
    es: {
      subject: "Actualizacion de caso de soporte",
      emailTitle: "Caso de soporte actualizado",
      emailText: "Se ha actualizado un caso de soporte de tu reserva.",
      emailTextClient:
        "Nuestro equipo de soporte se pondra en contacto contigo lo antes posible para resolverlo.",
      stateSolvedLabel:
        "Su caso de soporte ha sido marcado como resuelto. Si consideras que tu incidencia no ha sido resuelta, por favor, ponte en contacto con nuestro equipo de soporte a traves de la plataforma.",
    },
    en: {
      subject: "Support case updated",
      emailTitle: "Support case updated",
      emailText: "A support case for your reservation has been updated.",
      emailTextClient:
        "Our support team will get in touch with you as soon as possible to resolve it.",
      stateSolvedLabel:
        "Your support case has been marked as solved. If you consider that your issue has not been resolved, please contact our support team through the platform.",
    },
  },
  deleted: {
    es: {
      subject: "Caso de soporte eliminado",
      emailTitle: "Caso de soporte eliminado",
      emailText: "Se ha eliminado un caso de soporte de tu reserva.",
      stateSolvedLabel:
        "Su caso de soporte ha sido marcado como resuelto. Si consideras que tu incidencia no ha sido resuelta, por favor, ponte en contacto con nuestro equipo de soporte a traves de la plataforma.",
    },
    en: {
      subject: "Support case deleted",
      emailTitle: "Support case deleted",
      emailText: "A support case for your reservation has been deleted.",
      stateSolvedLabel:
        "Your support case has been marked as solved. If you consider that your issue has not been resolved, please contact our support team through the platform.",
    },
  },
};

const getTextValue = (statusConfig, fieldKey, locale) => {
  const value = statusConfig?.texts?.[fieldKey]?.[locale];
  return typeof value === "string" ? value : "";
};

const getDefaultTextPlaceholder = (statusKey, fieldKey, locale) => {
  const stateKey = SUPPORT_CASE_DEFAULT_TEXTS[statusKey] ? statusKey : "new";
  const safeLocale = locale === "en" ? "en" : "es";
  return SUPPORT_CASE_DEFAULT_TEXTS[stateKey]?.[safeLocale]?.[fieldKey] || "";
};

const buildTextsByFieldAndLocale = (statusConfig = {}) =>
  Object.fromEntries(
    SUPPORT_CASE_TEXT_FIELDS.map((field) => [
      field.key,
      Object.fromEntries(
        SUPPORTED_LOCALES.map((locale) => [locale, getTextValue(statusConfig, field.key, locale)])
      ),
    ])
  );

export const buildSupportCaseStatesPayload = (statesSource) =>
  Object.fromEntries(
    SUPPORT_CASE_STATES.map((statusKey) => {
      const statusConfig = statesSource?.[statusKey] || {};

      return [
        statusKey,
        {
          enabled: Boolean(statusConfig?.enabled),
          texts: buildTextsByFieldAndLocale(statusConfig),
        },
      ];
    })
  );

export const buildSupportCaseExpandedStates = () =>
  Object.fromEntries(SUPPORT_CASE_STATES.map((stateKey) => [stateKey, true]));

function SupportCaseTextFields({ statusKey, t }) {
  const visibleTextFields = SUPPORT_CASE_TEXT_FIELDS.filter((field) => {
    if (field.key === "emailTextClient" && statusKey === "deleted") return false;
    if (field.key === "stateSolvedLabel" && statusKey !== "update") return false;
    return true;
  });

  return (
    <Grid container spacing={2} sx={{ marginLeft: 5 }}>
      {visibleTextFields.map((field) => (
        <Grid size={{ xs: 12 }} key={`${statusKey}-${field.key}`}>
          <MDTypography
            variant="button"
            fontWeight="medium"
            mb={1}
            display="block"
            sx={{ textTransform: "uppercase", fontSize: 14, marginTop: 3 }}
          >
            {t(field.labelKey)}
          </MDTypography>
          <Grid container spacing={2}>
            {SUPPORTED_LOCALES.map((locale) => (
              <Grid size={{ xs: 12, md: 6 }} key={locale}>
                <Field
                  name={`emailTemplates.supportCase.states.${statusKey}.texts.${field.key}.${locale}`}
                  component={FormField}
                  label={`${t(field.labelKey)} (${locale.toUpperCase()})`}
                  placeholder={getDefaultTextPlaceholder(statusKey, field.key, locale)}
                  multiline
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
}

SupportCaseTextFields.propTypes = {
  statusKey: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

function SupportCaseStatusSection({ statusKey, isExpanded, onToggleExpand, t }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <Grid size={{ xs: 12 }} key={statusKey}>
      <MDBox
        p={2}
        borderRadius="lg"
        style={{
          border: darkMode ? "1px solid #26304b" : "1px solid #e5e7eb",
          background: darkMode ? "#26304b" : "#f3f4f6",
        }}
      >
        <Field
          name={`emailTemplates.supportCase.states.${statusKey}.enabled`}
          component={({ input: statusInput }) => (
            <>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Checkbox
                  checked={!!statusInput.value}
                  onChange={(event) => statusInput.onChange(event.target.checked)}
                />
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: 16,
                    textTransform: "uppercase",
                    color: darkMode ? "#f8fafc" : "#0f172a",
                  }}
                  onClick={() => onToggleExpand(statusKey)}
                >
                  {t(SUPPORT_CASE_STATUS_LABEL_KEYS[statusKey])}
                </MDTypography>
              </MDBox>

              {statusInput.value && isExpanded && (
                <SupportCaseTextFields statusKey={statusKey} t={t} />
              )}
            </>
          )}
        />
      </MDBox>
    </Grid>
  );
}

SupportCaseStatusSection.propTypes = {
  statusKey: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function SupportCaseEmailSection({
  expandedStates,
  isTemplateExpanded,
  onToggleStateExpanded,
  onToggleTemplateExpanded,
  t,
  templateInput,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <Checkbox
            checked={!!templateInput.value}
            onChange={(event) => templateInput.onChange(event.target.checked)}
          />
          <MDTypography
            variant="button"
            fontWeight="medium"
            textTransform="uppercase"
            sx={{
              cursor: "pointer",
              userSelect: "none",
              fontSize: 18,
              color: darkMode ? "#f8fafc" : "#0f172a",
            }}
            onClick={onToggleTemplateExpanded}
          >
            {t("emailSupportCase")}
          </MDTypography>
        </MDBox>
      </Grid>

      {templateInput.value &&
        isTemplateExpanded &&
        SUPPORT_CASE_STATES.map((statusKey) => (
          <SupportCaseStatusSection
            key={statusKey}
            statusKey={statusKey}
            isExpanded={!!expandedStates[statusKey]}
            onToggleExpand={onToggleStateExpanded}
            t={t}
          />
        ))}
    </>
  );
}

SupportCaseEmailSection.propTypes = {
  expandedStates: PropTypes.object.isRequired,
  isTemplateExpanded: PropTypes.bool.isRequired,
  onToggleStateExpanded: PropTypes.func.isRequired,
  onToggleTemplateExpanded: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  templateInput: PropTypes.shape({
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
};

export default SupportCaseEmailSection;
